import type ru from './ru';

const en: typeof ru = {
  common: {
    loading: 'Loading…',
    error: 'Something went wrong. Please try again.',
    retry: 'Retry',
    cancel: 'Cancel',
    save: 'Save',
    done: 'Done',
    close: 'Close',
    more: 'Show more',
    showAll: 'Show all',
    nights: 'night | nights',
    guests: 'guest | guests',
    months: 'month | months',
    perNight: 'night',
    perMonth: 'month',
    total: 'total',
    bedrooms: 'bedroom | bedrooms',
    beds: 'bed | beds',
    baths: 'bath | baths',
    new: 'new',
    reviewsCount: '{n} review | {n} reviews',
  },
  tabs: { explore: 'Explore', favorites: 'Wishlist', trips: 'Trips', profile: 'Profile' },
  explore: {
    title: 'Where to?',
    where: 'where',
    wherePlaceholder: 'City or area',
    when: 'when',
    anyDates: 'Any dates',
    who: 'guests',
    short: 'Nightly',
    long: 'Long stay',
    search: 'Search stays',
    destinations: 'popular destinations',
    stays: 'stays',
    results: '{n} found',
    empty: 'Nothing found. Try other dates or another city.',
    filters: 'Filters',
    map: 'Map',
    list: 'List',
    sort: { recommended: 'Recommended', price_asc: 'Price: low to high', price_desc: 'Price: high to low', rating: 'Top rated', newest: 'Newest' },
    priceMax: 'max price, {currency}',
    bedroomsMin: 'bedrooms',
    propertyType: 'property type',
    apply: 'Show',
    reset: 'Reset',
    listingsIn: '{n} stays',
  },
  dates: {
    title: 'Dates',
    checkIn: 'check-in',
    checkOut: 'check-out',
    pickCheckIn: 'Pick check-in date',
    pickCheckOut: 'Pick check-out date',
    clear: 'Clear',
    apply: 'Done',
  },
  listing: {
    hostedBy: 'Hosted by {name}',
    hostReply: 'Host reply',
    agency: 'management company',
    individual: 'private owner',
    verified: 'verified',
    about: 'about this place',
    amenities: 'amenities',
    rules: 'house rules',
    checkIn: 'Check-in from {time}',
    checkOut: 'Check-out until {time}',
    pets: 'Pets allowed',
    noPets: 'No pets',
    smoking: 'Smoking allowed',
    noSmoking: 'No smoking',
    events: 'Events allowed',
    noEvents: 'No parties',
    children: 'Suitable for children',
    location: 'location',
    locationHint: 'The exact address comes after booking.',
    reviews: 'reviews',
    noReviews: 'No reviews yet',
    cancellation: 'cancellation policy',
    availability: 'availability',
    from: 'from',
    reserve: 'Reserve',
    chooseDates: 'Choose dates',
    instant: 'Instant booking',
    unavailable: 'Dates unavailable',
    longTermNote: 'Monthly stay: paid month by month, deposit {deposit}',
    utilitiesIncluded: 'utilities included',
    idRequired: 'The host accepts guests with a verified ID',
    minNights: 'Minimum {n} nights',
    propertyTypes: { apartment: 'Apartment', studio: 'Studio', house: 'House', townhouse: 'Townhouse', villa: 'Villa', penthouse: 'Penthouse', room: 'Room', other: 'Place' },
    spaceTypes: { entire: 'entire place', private_room: 'private room', shared_room: 'shared room' },
    policies: {
      flexible: 'Flexible: full refund if cancelled at least 24 hours before check-in.',
      moderate: 'Moderate: full refund until 5 days before check-in, then 50% of the stay.',
      firm: 'Firm: full refund until 30 days before, 50% until 7 days, then no refund.',
      strict: 'Strict: full refund within 48h of booking (if check-in is 14+ days away), 50% until 7 days.',
      long_term: 'Monthly: full refund until 30 days before check-in, then the first month is non-refundable.',
      non_refundable: 'Non-refundable: the stay is not refunded after booking.',
      short_notice: 'Full refund until 24 hours before, 50% until 2 hours before.',
    },
  },
  checkout: {
    title: 'Confirm and pay',
    yourTrip: 'your trip',
    dates: 'Dates',
    guests: 'Guests',
    edit: 'Edit',
    price: 'price details',
    accommodation: '{nights} × stay',
    monthlyStay: 'Stay, {months} mo.',
    discount: 'Discount',
    extraGuests: 'Extra guests',
    cleaning: 'Cleaning',
    serviceFee: 'Service fee',
    deposit: 'Deposit (refundable)',
    total: 'Total',
    payNow: 'Pay now',
    payOnSite: 'Pay the host on arrival',
    payment: 'payment',
    payOnline: 'Pay in full online',
    payOnlineHint: 'By card now, secured by Stripe.',
    payOnsite: 'Prepay + on arrival',
    payOnsiteHint: 'Only the service fee now, the rest to the host on arrival.',
    installments: 'payment schedule',
    installment: 'Month {n} · {date}',
    contact: 'contact details',
    name: 'full name',
    phone: 'phone',
    sharePhone: 'Share my number from Telegram',
    phoneVerified: 'verified',
    phoneFromLogin: 'The phone you signed in with is shared with the host.',
    message: 'message to the host',
    messagePlaceholder: 'When you arrive, who you travel with',
    policy: 'cancellation policy',
    agree: 'By paying you agree to the house rules and the cancellation policy.',
    confirmPay: 'Pay {amount}',
    idRequired: 'This host only accepts guests with a verified ID.',
    verifyId: 'Verify my ID',
    paymentTitle: 'Payment',
    holdNote: 'The dates are held for you for {minutes} minutes.',
    paying: 'Paying…',
    stripeMissing: 'Online payment is not configured on the server (STRIPE_PUBLISHABLE_KEY).',
  },
  success: {
    title: 'You’re booked!',
    waiting: 'Confirming the payment…',
    confirmed: 'Your booking is confirmed. Details are in Telegram; check-in instructions will arrive before your stay.',
    openTrip: 'Open trip',
  },
  trips: {
    title: 'Trips',
    upcoming: 'Upcoming',
    past: 'Past',
    cancelled: 'Cancelled',
    empty: 'Your trips will appear here',
    findStay: 'Find a stay',
    status: {
      pending_payment: 'awaiting payment',
      confirmed: 'confirmed',
      in_progress: 'staying',
      completed: 'completed',
      cancelled_by_guest: 'cancelled by you',
      cancelled_by_host: 'cancelled by host',
      cancelled_by_admin: 'cancelled by platform',
      expired: 'not paid',
    },
  },
  trip: {
    code: 'booking {code}',
    address: 'address',
    openMaps: 'Open in maps',
    addressAfter: 'The address unlocks after payment.',
    host: 'host',
    call: 'Call',
    checkin: 'check-in',
    guide: 'Check-in instructions',
    guideLater: 'Instructions unlock closer to arrival — we will also send them to Telegram.',
    accessCode: 'Access code',
    payment: 'payment',
    paidOnline: 'Paid online',
    dueOnsite: 'Pay the host on arrival',
    payNow: 'Complete payment',
    holdExpires: 'The booking waits for payment until {time}',
    installments: 'monthly payments',
    payInstallment: 'Pay',
    cancel: 'Cancel booking',
    cancelTitle: 'Cancel booking',
    refund: 'You get back: {amount}',
    refundNone: 'No refund under the cancellation policy.',
    reason: 'Reason (optional)',
    cancelConfirm: 'Cancel booking',
    review: 'Write a review',
    yourReview: 'Your review',
    support: 'Need help? Contact support',
    cancelled: 'Booking cancelled',
  },
  review: {
    title: 'Review',
    overall: 'overall',
    cleanliness: 'cleanliness',
    accuracy: 'accuracy',
    checkin: 'check-in',
    communication: 'communication',
    location: 'location',
    value: 'value',
    public: 'tell other guests',
    private: 'private note to the host',
    submit: 'Publish',
    doubleBlind: 'The host sees your review once they review you, or 14 days after check-out.',
    thanks: 'Thanks for the review!',
  },
  favorites: { title: 'Wishlist', empty: 'Tap ♡ on a place you like — it will show up here.' },
  profile: {
    title: 'Profile',
    guest: 'guest',
    phone: 'phone',
    notVerified: 'not verified',
    verified: 'verified',
    sharePhone: 'Verify via Telegram',
    identity: 'ID',
    identityVerified: 'verified',
    identityVerify: 'Verify my ID',
    identityHint: 'Some hosts only accept guests with a verified ID. Stripe runs the check; we never store your documents.',
    notifications: 'notifications',
    notificationsOn: 'The bot can send you notifications',
    notificationsOff: 'Allow bot notifications',
    language: 'language',
    name: 'first name',
    lastName: 'last name',
    email: 'email',
    support: 'Support',
    host: 'Renting out a place? Host app →',
    login: 'Log in',
  },
  login: {
    title: 'Log in',
    hint: 'Enter your phone number — we will send a code.',
    phone: 'phone',
    getCode: 'Get code',
    code: 'code',
    openTelegram: 'Open Telegram to get the code',
    confirm: 'Log in',
    devCode: 'Dev mode: code {code}',
  },
  support: {
    title: 'Support',
    topic: 'topic',
    topics: { booking: 'Booking', payment: 'Payment', checkin: 'Check-in', account: 'Account', other: 'Other' },
    text: 'message',
    send: 'Send',
    sent: 'Message sent — we will reply in Telegram.',
  },
  errors: {
    dates_unavailable: 'These dates are no longer available. Pick others.',
    payment_unavailable: 'Payment could not be started. Try again later.',
    too_many_guests: 'Too many guests for this place.',
    min_nights: 'The stay is too short for this place.',
    max_nights: 'The stay is too long — choose a monthly stay.',
    min_months: 'The minimum monthly stay is longer than selected.',
    advance_notice: 'These dates are too soon — the host asks to book in advance.',
    booking_window: 'These dates are not open for booking yet.',
    identity_verification_required: 'This host only accepts guests with a verified ID.',
    phone_required: 'Add a phone number so the host can reach you.',
    onsite_not_allowed: 'This host accepts online payment only.',
    rental_type_unavailable: 'This type of stay is not available here.',
    listing_unavailable: 'This place is not available for booking now.',
    not_cancellable: 'This booking can no longer be cancelled.',
    review_closed: 'The review is already written or the window has closed.',
    guide_not_ready: 'Instructions unlock closer to arrival.',
    invalid_code: 'Wrong code.',
    customer_not_verified: 'This host only accepts guests with a verified ID.',
    nightly_not_available: 'This place is rented monthly only.',
    monthly_not_available: 'This place is not rented monthly.',
    payment_expired: 'The time to pay has run out. Book again.',
    payment_not_configured: 'Payments and ID checks are not connected yet. Contact support.',
    channel_unavailable: 'Open our Telegram bot, share your phone there and request the code again.',
    booking_state: 'This booking can no longer be changed.',
    throttled: 'Too many attempts. Try again in a minute.',
  },
  boot: {
    error: 'Could not sign in with Telegram. Close and reopen the app.',
  },
  // Placeholders: an example of what to type in each field.
  ph: {
    priceMax: '300',
    phone: '+971 50 000 00 00',
    code: '123456',
    supportText: 'Booking EM12345: the door code from the guide does not work.',
    review: 'Spotless apartment, amazing view and an easy check-in. Would stay again!',
    reviewPrivate: 'The shower drains slowly — worth a look.',
    fullName: 'Anna Smith',
    firstName: 'Anna',
    lastName: 'Smith',
    email: "anna{'@'}example.com", // a bare @ is vue-i18n linked-message syntax: the production build fails to render the screen
    cancelReason: 'Our plans changed — we can no longer travel on these dates.',
  },
};

export default en;
